import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import { useQuery } from "wasp/client/operations";
import { getLocations } from "wasp/client/operations";
import {
  useParams,
  useLocation as useRouterLocation,
  useNavigate,
} from "react-router-dom";
import type { Location, User } from "wasp/entities";
import { useAuth } from "wasp/client/auth";

// Extended Location type with hierarchy fields
export interface LocationWithHierarchy extends Location {
  parentLocation?: { id: string; name: string; locationCode: string } | null;
  childLocations?: {
    id: string;
    name: string;
    locationCode: string;
    isHub?: boolean;
  }[];
  _count?: { childLocations: number };
  allChildLocationIds?: string[];
}

interface LocationContextType {
  selectedLocation: LocationWithHierarchy | null;
  setSelectedLocation: (location: LocationWithHierarchy | null) => void;
  locations: LocationWithHierarchy[];
  isLoading: boolean;
  error: any;
  navigateToLocationPage: (path: string) => void;
  // Hub filtering
  childLocationFilter: string | null;
  setChildLocationFilter: (locationId: string | null) => void;
  isHubSelected: boolean;
  childLocations: LocationWithHierarchy[];
  // Helper to get all location IDs for current selection (for queries)
  getLocationIdsForQuery: () => string[];
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

interface LocationProviderProps {
  children: ReactNode;
}

export function LocationProvider({ children }: LocationProviderProps) {
  const [selectedLocation, setSelectedLocationInternal] =
    useState<LocationWithHierarchy | null>(() => {
      // Try to restore from localStorage on initial load
      const saved = localStorage.getItem("selectedLocation");
      return saved ? JSON.parse(saved) : null;
    });
  const [childLocationFilter, setChildLocationFilter] = useState<string | null>(
    null,
  );
  const params = useParams<{ slug?: string }>();
  const routerLocation = useRouterLocation();
  const { data: user } = useAuth();
  const navigate = useNavigate();

  // Fetch available locations
  const { data: locationsData, isLoading, error } = useQuery(getLocations);
  const locations = (locationsData as unknown as LocationWithHierarchy[]) || [];

  // Custom setter that clears child filter when location changes
  const setSelectedLocation = (location: LocationWithHierarchy | null) => {
    setSelectedLocationInternal(location);
    setChildLocationFilter(null); // Clear filter when switching locations
  };

  // Persist selected location to localStorage whenever it changes
  useEffect(() => {
    if (selectedLocation) {
      localStorage.setItem(
        "selectedLocation",
        JSON.stringify(selectedLocation),
      );
    }
  }, [selectedLocation]);

  const navigateToLocationPage = (path: string) => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}${path}`);
    } else {
      navigate(path);
    }
  };

  // Set initial selected location based on URL params or primary location or first available location
  useEffect(() => {
    if (locations && Array.isArray(locations) && locations.length > 0) {
      if (params.slug) {
        // Find location by slug (locationCode)
        const locationBySlug = locations.find(
          (loc) => loc.locationCode === params.slug,
        );
        if (locationBySlug) {
          setSelectedLocationInternal(locationBySlug);
        } else {
          // Fallback to primary location or first location if slug not found
          const primaryLoc = user?.primaryLocationId
            ? locations.find(loc => loc.id === user.primaryLocationId)
            : null;
          setSelectedLocationInternal(primaryLoc || locations[0]);
        }
      } else if (!selectedLocation) {
        // No slug in URL and no selected location, use primary location if available
        const primaryLoc = user?.primaryLocationId
          ? locations.find(loc => loc.id === user.primaryLocationId)
          : null;
        setSelectedLocationInternal(primaryLoc || locations[0]);
      }
    }
  }, [locations, params.slug, user?.primaryLocationId]);

  // Reset selected location when navigating to non-location-specific pages
  useEffect(() => {
    const isLocationSpecificRoute =
      routerLocation.pathname.includes("/locations/") ||
      routerLocation.pathname.includes("/tenants") ||
      routerLocation.pathname.includes("/notices") ||
      routerLocation.pathname.includes("/events") ||
      routerLocation.pathname.includes("/facilities") ||
      routerLocation.pathname.includes("/buy-sell") ||
      routerLocation.pathname.includes("/food-court") ||
      routerLocation.pathname.includes("/lost-found") ||
      routerLocation.pathname.includes("/retail") ||
      routerLocation.pathname.includes("/services") ||
      routerLocation.pathname.includes("/request-offer") ||
      routerLocation.pathname.includes("/dashboard") ||
      routerLocation.pathname.includes("/admin");

    if (!isLocationSpecificRoute && locations.length > 0) {
      setSelectedLocationInternal(locations[0]);
    }
  }, [routerLocation.pathname, locations]);

  // Compute if current selection is a hub
  const isHubSelected = useMemo(() => {
    return selectedLocation?.isHub === true;
  }, [selectedLocation]);

  // Get child locations for the currently selected hub
  const childLocations = useMemo(() => {
    if (!selectedLocation?.isHub) return [];
    // Get all locations that have this location as parent
    return locations.filter(
      (loc) => loc.parentLocationId === selectedLocation.id,
    );
  }, [selectedLocation, locations]);

  // Helper to get all location IDs for queries (handles hub aggregation)
  const getLocationIdsForQuery = (): string[] => {
    if (!selectedLocation) return [];

    if (childLocationFilter) {
      // Specific child location filter is active
      return [childLocationFilter];
    }

    if (selectedLocation.isHub && selectedLocation.allChildLocationIds) {
      // Return hub ID + all descendant IDs
      return [selectedLocation.id, ...selectedLocation.allChildLocationIds];
    }

    // Regular location
    return [selectedLocation.id];
  };

  const value: LocationContextType = {
    selectedLocation,
    setSelectedLocation,
    locations,
    isLoading,
    error,
    navigateToLocationPage,
    // Hub filtering
    childLocationFilter,
    setChildLocationFilter,
    isHubSelected,
    childLocations,
    getLocationIdsForQuery,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error(
      "useLocationContext must be used within a LocationProvider",
    );
  }
  return context;
}

// Keep this for backwards compatibility but prefer useLocationContext
export function useLocation(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
}
